import { createObservableStore } from "../observable.ts";
import type { DiscoverCacheStore } from "../ports/discoverCache.ts";
import type { CredentialStore, SettingsRecord, SettingsStore } from "../ports/settings.ts";
import type { NetworkStatus } from "../ports/platform.ts";

export interface SettingsState {
  settings: SettingsRecord;
  hasKey: boolean;
  busy: boolean;
  message?: string;
}

export class SettingsController {
  private readonly store = createObservableStore<SettingsState>({
    settings: { aiMode: "gemini" },
    hasKey: false,
    busy: false,
  });

  constructor(
    private readonly settingsStore: SettingsStore,
    private readonly credentials: CredentialStore,
    private readonly network: NetworkStatus,
    private readonly testKey: (apiKey: string, signal?: AbortSignal) => Promise<void>,
    private readonly discoverCache: DiscoverCacheStore,
  ) {}

  subscribe = this.store.subscribe.bind(this.store);
  getSnapshot = this.store.getSnapshot.bind(this.store);

  async hydrate(): Promise<void> {
    const settings = await this.settingsStore.get();
    const record = await this.credentials.get();
    this.store.setState({
      settings,
      hasKey: Boolean(record?.apiKey),
      busy: false,
    });
  }

  async saveMode(settings: SettingsRecord): Promise<void> {
    await this.settingsStore.save(settings);
    this.store.setState((current) => ({ ...current, settings, message: "Preferences saved." }));
  }

  async saveKey(apiKey: string): Promise<void> {
    const trimmed = apiKey.trim();
    if (!trimmed) {
      this.store.setState((current) => ({
        ...current,
        message: "Enter an API key before saving.",
      }));
      return;
    }
    await this.credentials.save({ provider: "gemini", apiKey: trimmed });
    this.store.setState((current) => ({
      ...current,
      hasKey: true,
      message: "API key saved on this device only.",
    }));
  }

  async removeKey(): Promise<void> {
    await this.credentials.clear();
    this.store.setState((current) => ({
      ...current,
      hasKey: false,
      message: "API key removed from this device.",
    }));
  }

  async clearDiscoverCache(): Promise<void> {
    await this.discoverCache.clear();
    this.store.setState((current) => ({
      ...current,
      message: "Meaning cache cleared.",
    }));
  }

  async test(apiKey?: string): Promise<void> {
    const record = await this.credentials.get();
    const key = apiKey?.trim() || record?.apiKey;
    if (!key) {
      this.store.setState((current) => ({
        ...current,
        message: "Add an API key first.",
      }));
      return;
    }
    if (!this.network.isOnline()) {
      this.store.setState((current) => ({
        ...current,
        message: "Testing a key needs internet.",
      }));
      return;
    }
    this.store.setState((current) => ({ ...current, busy: true, message: "Testing key…" }));
    try {
      await this.testKey(key);
      this.store.setState((current) => ({
        ...current,
        busy: false,
        message: "The key works with Gemini.",
      }));
    } catch (error) {
      this.store.setState((current) => ({
        ...current,
        busy: false,
        message: error instanceof Error ? error.message : "The key could not be verified.",
      }));
    }
  }
}
